import React, { FC, useCallback, useEffect, useMemo, useState } from "react";

import {
  ARCHIVED_CLIENT_TIP,
  getInitialsFromFullName,
  inactiveStatusId,
} from "../../../../../../constants/constants";
import { WorkModel } from "../../../../../../factories/works/models/work.model";
import { useWorksSearchParams } from "../../../../../../hooks/useWorksSearchParams";
import { useAppSelector } from "../../../../../../redux/redux";
import { getLinkedFirm } from "../../../../../../sessionStorage/sessionStorage";
import {
  useArchiveOrUnarchiveMutation,
  useGetWorksQuery,
  useUpdateMultipleStatusesMutation,
} from "../../../../../../slices/WorkSlice";
import { IOrganizationContact } from "../../../../../../types/types";
import { WorkList } from "../../../../../works/components/WorkList";
import { WorksArchiveConfirmModal } from "../../../../../works/components/WorksArchiveConfirmModal";
import { WorksBulkActions } from "../../../../../works/components/WorksBulkActions";
import { AddWorkModal } from "../../../../../works/modals/AddWork.modal";
import {
  EMPTY_WORKS_FILTERS,
  WorksFilters,
} from "../../../../../works/types/work-filters";
import { WorkTableSort } from "../../../../../works/types/work-table";
import { mapWorksFiltersToQuery } from "../../../../../works/utils/mapWorksFiltersToQuery";
import { MULTIPLE_STATUSES } from "../../../../../works/utils/statuses";
import { ContactWorksFilters } from "../../../../components/ContactWorksFilters";
import ErrorPopup from "../../../../modals/ErrorPopup";

type Props = {
  organization: IOrganizationContact;
};

export const OrganizationWorks: FC<Props> = ({ organization }) => {
  const { user } = useAppSelector((state) => state.appReducer);

  const { page, sortBy, sortOrder, setPage, setSort } = useWorksSearchParams({
    defaultSortBy: WorkTableSort.StartDate,
    defaultSortOrder: "desc",
  });

  const [filters, setFilters] = useState<WorksFilters>(EMPTY_WORKS_FILTERS);
  const [isAddWork, setIsAddWork] = useState(false);
  const [isOpenError, setIsOpenError] = useState(false);
  const [selectedWorks, setSelectedWorks] = useState<WorkModel[]>([]);
  const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);
  const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false);
  const [isHideGreenStatus, setIsHideGreenStatus] = useState(false);

  const [archiveOrUnarchive, { isLoading: isArchiveLoading }] =
    useArchiveOrUnarchiveMutation();

  const [updateStatuses, { isLoading: isUpdatingStatuses }] =
    useUpdateMultipleStatusesMutation();

  const isActive = organization?.contactStatusEnumId !== inactiveStatusId;
  const hasSelectedWorks = selectedWorks.length > 0;

  const mappedFilters = useMemo(
    () => mapWorksFiltersToQuery(filters),
    [filters],
  );

  const worksArgs = useMemo(
    () => ({
      ...mappedFilters,
      pageNumber: page,
      pageSize: 20,
      orgId: getLinkedFirm()?.orgId || "",
      sortBy,
      sortOrder,
      customerIds: [organization.customerId || ""],
      currentUserId: user.id,
      hideGreenStatus: isHideGreenStatus,
    }),
    [
      mappedFilters,
      page,
      sortBy,
      sortOrder,
      organization.customerId,
      user.id,
      isHideGreenStatus,
    ],
  );

  const { data: works = [], isFetching: isLoadingWorks } = useGetWorksQuery(
    worksArgs,
    {
      skip: !organization.customerId || !user.id,
    },
  );

  const totalRecords = works?.[0]?.totalRecords ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRecords / 20));

  const handleArchiveConfirm = useCallback(async () => {
    if (!hasSelectedWorks) return;

    await archiveOrUnarchive({
      body: selectedWorks.map((item) => item.workId),
      archiveWork: true,
    }).unwrap();

    setSelectedWorks([]);
    setIsArchiveConfirmOpen(false);
  }, [archiveOrUnarchive, hasSelectedWorks, selectedWorks]);

  const handleBulkStatusChange = useCallback(
    async (status: string) => {
      if (!hasSelectedWorks) return;

      await updateStatuses({
        body: selectedWorks.map((item) => item.workId),
        status,
      }).unwrap();

      setSelectedWorks([]);
      setIsStatusMenuOpen(false);
    },
    [hasSelectedWorks, selectedWorks, updateStatuses],
  );

  useEffect(() => {
    setSelectedWorks([]);
    setIsStatusMenuOpen(false);
  }, [page, sortBy, sortOrder, filters, isHideGreenStatus]);

  useEffect(() => {
    if (!hasSelectedWorks && isStatusMenuOpen) {
      setIsStatusMenuOpen(false);
    }
  }, [hasSelectedWorks, isStatusMenuOpen]);

  return (
    <>
      <WorkList
        works={works}
        archiveWorks={selectedWorks}
        setArchiveWorks={setSelectedWorks}
        isLoading={isLoadingWorks || isArchiveLoading || isUpdatingStatuses}
        hasPages
        page={page}
        totalPages={totalPages}
        total={totalRecords}
        pageSize={20}
        onPageChange={setPage}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={setSort}>
        <div className={"w-full px-8 py-6"}>
          <div className={"mb-2 text-sm font-bold"}>
            Total Work: {totalRecords}
          </div>

          <div className={"flex items-center justify-between gap-4"}>
            <ContactWorksFilters filters={filters} setFilters={setFilters} />

            <WorksBulkActions
              hasSelectedWorks={hasSelectedWorks}
              isStatusMenuOpen={isStatusMenuOpen}
              setIsStatusMenuOpen={setIsStatusMenuOpen}
              multipleStatuses={MULTIPLE_STATUSES}
              onStatusChange={handleBulkStatusChange}
              onArchiveClick={() => setIsArchiveConfirmOpen(true)}
              onAddWorkClick={() => isActive && setIsAddWork(true)}
              addWorkDisabledReason={isActive ? null : ARCHIVED_CLIENT_TIP}
              isHideGreenStatus={isHideGreenStatus}
              setIsHideGreenStatus={setIsHideGreenStatus}
              actionsClassName={"grid-cols-[repeat(4,max-content)]"}
              actions={[
                "show-completed",
                "change-status",
                "archive-icon",
                "add-work",
              ]}
            />
          </div>
        </div>
      </WorkList>

      {isAddWork && (
        <AddWorkModal
          isOpen={isAddWork}
          onClose={() => setIsAddWork(false)}
          defaultContact={{
            value: organization.customerId || "",
            label: organization.companyName,
            avatar: getInitialsFromFullName(organization.companyName),
            avatarColor: "purple",
          }}
        />
      )}

      <WorksArchiveConfirmModal
        isOpen={isArchiveConfirmOpen}
        selectedWorks={selectedWorks}
        onClose={() => setIsArchiveConfirmOpen(false)}
        onConfirm={handleArchiveConfirm}
      />

      <ErrorPopup
        closePopup={() => setIsOpenError(false)}
        isOpen={isOpenError}
      />
    </>
  );
};
